import { useEffect, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormDescription,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { Switch } from '@/common/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Plus, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { DateTimePicker } from '@/common/components/DateTimePicker'
import ImageUploadModal from '@/common/components/ImageUploadModal'
import { FileService } from '@/core/api/services/FileService'
import useMutationCreateTournament from '@/modules/tournament/api/hooks/useMutationCreateTournament'
import useMutationUpdateTournament from '@/modules/tournament/api/hooks/useMutationUpdateTournament'
import useMutationGetTournamentBannerUploadUrl from '@/core/api/hooks/useMutationGetTournamentBannerUploadUrl'
import useQueryGetGameList from '@/modules/game/api/hooks/useQueryGetGameList'
import useQueryGetGameProviderList from '@/modules/game/api/hooks/useQueryGetGameProviderList'
import { Badge } from '@/common/ui/badge'
import { Checkbox } from '@/common/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import type { components } from '@/schema'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

type CreateTournamentBody = components['schemas']['AdminCreateTournamentDto']

const prizeSchema = z.object({
	rank: z.number().min(1, 'อันดับต้องมากกว่า 0'),
	prizeAmount: z.number().min(0, 'จำนวนเงินต้องไม่ติดลบ'),
})

const tournamentSchema = z
	.object({
		name: z.string().min(1, 'กรุณาระบุชื่อทัวร์นาเมนต์'),
		description: z.string().optional(),
		bannerImage: z.string().optional(),
		startDate: z.date({ required_error: 'กรุณาระบุวันเริ่มต้น' }),
		endDate: z.date({ required_error: 'กรุณาระบุวันสิ้นสุด' }),
		hasJoinConditions: z.boolean().default(false),
		minTurnover: z.number().min(0, 'ยอดเทิร์นขั้นต่ำต้องไม่ติดลบ').optional(),
		minDeposit: z.number().min(0, 'ยอดฝากขั้นต่ำต้องไม่ติดลบ').optional(),
		minBetAmount: z.number().min(0, 'เดิมพันขั้นต่ำต้องไม่ติดลบ'),
		maxTransactionDay: z.number().min(1, 'จำนวนธุรกรรมต่อวันต้องมากกว่า 0'),
		leaderboardRefresh: z
			.number()
			.min(60, 'เวลารีเฟรชต้องไม่น้อยกว่า 60 วินาที'),
		hasPointSystem: z.boolean().default(false),
		pointSystemConfig: z
			.object({
				streakBonuses: z.record(z.string(), z.number()),
			})
			.optional(),
		allowedGameIds: z.array(z.string()).optional(),
		allowedProviderIds: z.array(z.string()).optional(),
		isActive: z.boolean().default(true),
		prizes: z.array(prizeSchema).min(1, 'กรุณาเพิ่มรางวัลอย่างน้อย 1 รางวัล'),
	})
	.refine(
		(data) => {
			return data.endDate > data.startDate
		},
		{
			message: 'วันสิ้นสุดต้องมากกว่าวันเริ่มต้น',
			path: ['endDate'],
		},
	)

type TournamentFormData = z.infer<typeof tournamentSchema>

interface TournamentFormProps {
	mode: 'create' | 'edit'
	tournamentId?: string
	initialData?: Partial<TournamentFormData> & {
		allowedGames?: components['schemas']['AllowedGameResponse'][]
		allowedProviders?: components['schemas']['AllowedProviderResponse'][]
	}
	onSuccess?: () => void
	onCancel?: () => void
}

export default function TournamentForm({
	mode,
	tournamentId,
	initialData,
	onSuccess,
	onCancel,
}: TournamentFormProps) {
	const navigate = useNavigate()
	const [uploadedImageUrl, setUploadedImageUrl] = useState<string>('')
	const [gameSearchQuery, setGameSearchQuery] = useState('')
	const [providerSearchQuery, setProviderSearchQuery] = useState('')

	// Fetch games and providers for selection with search
	const { data: gamesData } = useQueryGetGameList({
		skip: 0,
		take: 100,
		name: gameSearchQuery || undefined,
	})
	const { data: providersData } = useQueryGetGameProviderList({
		skip: 0,
		take: 100,
		name: providerSearchQuery || undefined,
		gameType: 'CASINO',
	})

	const form = useForm<TournamentFormData>({
		resolver: zodResolver(tournamentSchema),
		defaultValues: {
			name: '',
			description: '',
			bannerImage: '',
			startDate: undefined,
			endDate: undefined,
			hasJoinConditions: false,
			minTurnover: undefined,
			minDeposit: undefined,
			minBetAmount: 100,
			maxTransactionDay: 30,
			leaderboardRefresh: 300,
			hasPointSystem: false,
			pointSystemConfig: {
				streakBonuses: {
					'3': 1,
					'5': 2,
					'7': 3,
					'10': 5,
					'15': 10,
				},
			},
			allowedGameIds: [],
			allowedProviderIds: [],
			isActive: true,
			prizes: [{ rank: 1, prizeAmount: 0 }],
		},
	})

	const { fields, append, remove } = useFieldArray({
		control: form.control,
		name: 'prizes',
	})

	const { mutateAsync: getPresignedUrl } =
		useMutationGetTournamentBannerUploadUrl()

	const createMutation = useMutationCreateTournament({
		onSuccess: () => {
			if (onSuccess) {
				onSuccess()
			} else {
				navigate('/operator/tournaments')
			}
		},
	})
	const updateMutation = useMutationUpdateTournament(tournamentId || '', {
		onSuccess: () => {
			if (onSuccess) {
				onSuccess()
			} else {
				navigate('/operator/tournaments')
			}
		},
	})

	const isPending = createMutation.isPending || updateMutation.isPending

	useEffect(() => {
		if (initialData && mode === 'edit') {
			const hasConditions =
				(initialData.minTurnover && initialData.minTurnover > 0) ||
				(initialData.minDeposit && initialData.minDeposit > 0)

			form.reset({
				name: initialData.name || '',
				description: initialData.description || '',
				bannerImage: initialData.bannerImage || '',
				startDate: initialData.startDate
					? new Date(initialData.startDate)
					: undefined,
				endDate: initialData.endDate
					? new Date(initialData.endDate)
					: undefined,
				hasJoinConditions: !!hasConditions,
				minTurnover: initialData.minTurnover,
				minDeposit: initialData.minDeposit,
				minBetAmount: initialData.minBetAmount || 100,
				maxTransactionDay: initialData.maxTransactionDay || 30,
				leaderboardRefresh: initialData.leaderboardRefresh || 300,
				hasPointSystem: !!initialData.pointSystemConfig,
				pointSystemConfig: initialData.pointSystemConfig || {
					streakBonuses: {
						'3': 1,
						'5': 2,
						'7': 3,
						'10': 5,
						'15': 10,
					},
				},
				allowedGameIds: initialData.allowedGames?.map((game) => game.id) || [],
				allowedProviderIds:
					initialData.allowedProviders?.map((provider) => provider.id) || [],
				isActive: initialData.isActive ?? true,
				prizes: initialData.prizes || [{ rank: 1, prizeAmount: 0 }],
			})
			setUploadedImageUrl(initialData.bannerImage || '')
		}
	}, [initialData, mode, form])

	// Handle image cropping and upload
	const handleImageCropped = async (imageBlob: Blob) => {
		try {
			// Check if the blob is a GIF - upload directly without conversion
			if (imageBlob.type === 'image/gif') {
				const urlData = await getPresignedUrl({ fileExtension: 'gif' })

				if (!urlData.presignedUrl || !urlData.fileUrl) {
					throw new Error('Failed to get upload URL')
				}

				const gifFile = new File([imageBlob], `banner_${Date.now()}.gif`, {
					type: 'image/gif',
				})
				await FileService.uploadFileToPresignedUrl(
					urlData.presignedUrl,
					gifFile,
				)

				const imageUrl = urlData.fileUrl
				form.setValue('bannerImage', imageUrl)
				setUploadedImageUrl(imageUrl)

				toast.success('อัปโหลด GIF สำเร็จ')
			} else {
				// Convert blob to WebP File with 75% quality for other image types
				const webpFile = await convertBlobToWebP(imageBlob, 0.75)

				const urlData = await getPresignedUrl({ fileExtension: 'webp' })

				if (!urlData.presignedUrl || !urlData.fileUrl) {
					throw new Error('Failed to get upload URL')
				}

				await FileService.uploadFileToPresignedUrl(
					urlData.presignedUrl,
					webpFile,
				)

				const imageUrl = urlData.fileUrl
				form.setValue('bannerImage', imageUrl)
				setUploadedImageUrl(imageUrl)

				toast.success('รูปภาพถูกอัปโหลดและแปลงเป็น WebP เรียบร้อยแล้ว')
			}
		} catch (error) {
			console.error('Upload failed:', error)
			showErrorToast('ไม่สามารถอัปโหลดรูปภาพได้ กรุณาลองใหม่อีกครั้ง')
			throw error
		}
	}

	// Convert blob to WebP format with specified quality
	const convertBlobToWebP = (
		blob: Blob,
		quality: number = 0.75,
	): Promise<File> => {
		return new Promise((resolve, reject) => {
			const canvas = document.createElement('canvas')
			const ctx = canvas.getContext('2d')
			const img = new Image()

			if (!ctx) {
				reject(new Error('Could not get canvas context'))
				return
			}

			img.onload = () => {
				canvas.width = img.width
				canvas.height = img.height

				ctx.imageSmoothingEnabled = true
				ctx.imageSmoothingQuality = 'high'

				ctx.drawImage(img, 0, 0)

				canvas.toBlob(
					(convertedBlob) => {
						if (convertedBlob) {
							const webpFile = new File(
								[convertedBlob],
								`banner_${Date.now()}.webp`,
								{
									type: 'image/webp',
									lastModified: Date.now(),
								},
							)
							resolve(webpFile)
						} else {
							reject(new Error('Failed to convert to WebP'))
						}
					},
					'image/webp',
					quality,
				)
			}

			img.onerror = () => reject(new Error('Failed to load image'))
			img.src = URL.createObjectURL(blob)
		})
	}

	const onSubmit = (data: TournamentFormData) => {
		const submitData: CreateTournamentBody = {
			name: data.name,
			description: data.description || undefined,
			bannerImage: data.bannerImage || uploadedImageUrl || undefined,
			startDate: data.startDate.toISOString(),
			endDate: data.endDate.toISOString(),
			minTurnover:
				data.hasJoinConditions && data.minTurnover
					? data.minTurnover
					: undefined,
			minDeposit:
				data.hasJoinConditions && data.minDeposit ? data.minDeposit : undefined,
			minBetAmount: data.minBetAmount,
			maxTransactionDay: data.maxTransactionDay,
			allowedGameTypes: 'CASINO',
			allowedGameIds: data.allowedGameIds?.length
				? data.allowedGameIds.join(',')
				: undefined,
			allowedProviderIds: data.allowedProviderIds?.length
				? data.allowedProviderIds.join(',')
				: undefined,
			leaderboardRefresh: data.leaderboardRefresh,
			pointSystemConfig: data.hasPointSystem
				? data.pointSystemConfig
				: undefined,
			prizes: data.prizes,
		}

		if (mode === 'create') {
			createMutation.mutate(submitData)
		} else if (mode === 'edit' && tournamentId) {
			updateMutation.mutate(submitData)
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					{mode === 'create' ? 'สร้างทัวร์นาเมนต์ใหม่' : 'แก้ไขทัวร์นาเมนต์'}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit, showFormValidationToast)}
						className="space-y-6"
					>
						<div className="space-y-4">
							<h3 className="text-lg font-semibold">ข้อมูลทั่วไป</h3>

							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>ชื่อทัวร์นาเมนต์</FormLabel>
										<FormControl>
											<Input
												placeholder="March 2026 Baccarat Championship"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>รายละเอียด</FormLabel>
										<FormControl>
											<Textarea
												placeholder="ระบุรายละเอียดทัวร์นาเมนต์"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="bannerImage"
								render={({ field }) => (
									<FormItem>
										<FormLabel>รูปแบนเนอร์ทัวร์นาเมนต์</FormLabel>
										<FormControl>
											<div className="space-y-3">
												<ImageUploadModal
													buttonText="อัปโหลดแบนเนอร์"
													aspectRatio={350 / 140}
													initialImageUrl={uploadedImageUrl || field.value}
													onImageCropped={handleImageCropped}
													acceptedFileTypes={[
														'image/jpeg',
														'image/png',
														'image/webp',
														'image/gif',
													]}
												/>
												{(uploadedImageUrl || field.value) && (
													<div className="rounded-lg border p-2">
														<img
															src={uploadedImageUrl || field.value}
															alt="Tournament banner preview"
															className="h-auto w-full rounded-md object-cover"
															style={{ maxHeight: '200px' }}
														/>
													</div>
												)}
											</div>
										</FormControl>
										<FormDescription>
											ขนาดแนะนำ 350x140 px (รองรับ JPG, PNG, WebP, GIF)
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="grid grid-cols-2 gap-4">
								<FormField
									control={form.control}
									name="startDate"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>วันที่เริ่มต้น</FormLabel>
											<FormControl>
												<DateTimePicker
													date={field.value}
													onDateSelect={field.onChange}
													placeholder="เลือกวันและเวลาเริ่มต้น"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="endDate"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>วันที่สิ้นสุด</FormLabel>
											<FormControl>
												<DateTimePicker
													date={field.value}
													onDateSelect={field.onChange}
													placeholder="เลือกวันและเวลาสิ้นสุด"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<FormField
								control={form.control}
								name="isActive"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
										<div className="space-y-0.5">
											<FormLabel className="text-base">เปิดใช้งาน</FormLabel>
											<FormDescription>
												ทัวร์นาเมนต์จะถูกแสดงในระบบหรือไม่
											</FormDescription>
										</div>
										<FormControl>
											<Switch
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
						</div>

						<div className="space-y-4">
							<FormField
								control={form.control}
								name="hasJoinConditions"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
										<div className="space-y-0.5">
											<FormLabel className="text-base">
												เงื่อนไขการเข้าร่วม
											</FormLabel>
											<FormDescription>
												กำหนดเงื่อนไขขั้นต่ำสำหรับผู้เล่นที่จะเข้าร่วม
											</FormDescription>
										</div>
										<FormControl>
											<Switch
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
									</FormItem>
								)}
							/>

							{form.watch('hasJoinConditions') && (
								<div className="grid grid-cols-2 gap-4">
									<FormField
										control={form.control}
										name="minTurnover"
										render={({ field }) => (
											<FormItem>
												<FormLabel>ยอดเทิร์นขั้นต่ำ</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder="0"
														{...field}
														onNumberChange={(value) => field.onChange(value)}
													/>
												</FormControl>
												<FormDescription>
													ยอดเทิร์นขั้นต่ำที่ต้องมีเพื่อเข้าร่วม
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="minDeposit"
										render={({ field }) => (
											<FormItem>
												<FormLabel>ยอดฝากขั้นต่ำ</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder="0"
														{...field}
														onNumberChange={(value) => field.onChange(value)}
													/>
												</FormControl>
												<FormDescription>
													ยอดฝากขั้นต่ำที่ต้องมีเพื่อเข้าร่วม
												</FormDescription>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							)}
						</div>

						<div className="space-y-4">
							<h3 className="text-lg font-semibold">กฎการแข่งขัน</h3>

							<div className="grid grid-cols-2 gap-4">
								<FormField
									control={form.control}
									name="minBetAmount"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>เดิมพันขั้นต่ำ</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder="100"
													{...field}
													onNumberChange={(value) => field.onChange(value)}
												/>
											</FormControl>
											<FormDescription>
												ยอดเดิมพันขั้นต่ำที่นับคะแนน (เฉพาะเกม CASINO)
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="maxTransactionDay"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>จำนวนธุรกรรมต่อวัน</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder="30"
													{...field}
													onNumberChange={(value) => field.onChange(value)}
												/>
											</FormControl>
											<FormDescription>
												จำนวนครั้งสูงสุดที่นับคะแนนต่อวัน
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<FormField
								control={form.control}
								name="leaderboardRefresh"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>
											เวลารีเฟรชกระดานคะแนน (วินาที)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="300"
												{...field}
												onNumberChange={(value) => field.onChange(value)}
											/>
										</FormControl>
										<FormDescription>
											ระยะเวลาในการอัปเดตกระดานคะแนน (ขั้นต่ำ 60 วินาที)
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="space-y-4">
							<h3 className="text-lg font-semibold">จำกัดเกมและค่าย</h3>

							<FormField
								control={form.control}
								name="allowedProviderIds"
								render={({ field }) => (
									<FormItem>
										<FormLabel>ค่ายเกมที่อนุญาต</FormLabel>
										<FormControl>
											<Popover>
												<PopoverTrigger asChild>
													<Button
														variant="outline"
														role="combobox"
														className="w-full justify-between"
													>
														<span className="truncate">
															{field.value && field.value.length > 0
																? `เลือกแล้ว ${field.value.length} ค่าย`
																: 'เลือกค่ายเกม (ไม่เลือก = อนุญาตทุกค่าย)'}
														</span>
													</Button>
												</PopoverTrigger>
												<PopoverContent className="w-full p-0" align="start">
													<div className="space-y-2 p-2">
														<Input
															placeholder="ค้นหาค่าย..."
															className="h-8 text-sm"
															value={providerSearchQuery}
															onChange={(e) =>
																setProviderSearchQuery(e.target.value)
															}
														/>
														<div className="max-h-[300px] space-y-1 overflow-y-auto">
															{providersData?.data.map((provider) => (
																<div
																	key={provider.id}
																	className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent"
																	onClick={() => {
																		const currentValue = field.value || []
																		const newValue = currentValue.includes(
																			provider.id,
																		)
																			? currentValue.filter(
																					(id) => id !== provider.id,
																				)
																			: [...currentValue, provider.id]
																		field.onChange(newValue)
																	}}
																>
																	<Checkbox
																		checked={field.value?.includes(provider.id)}
																		className="pointer-events-none h-4 w-4"
																	/>
																	<span className="text-xs">
																		{provider.displayName || provider.name}
																	</span>
																</div>
															))}
															{(!providersData?.data ||
																providersData.data.length === 0) && (
																<div className="py-2 text-center text-xs text-muted-foreground">
																	ไม่พบค่ายเกม
																</div>
															)}
														</div>
														{field.value && field.value.length > 0 && (
															<Button
																variant="ghost"
																className="w-full justify-center text-xs"
																onClick={() => field.onChange([])}
																type="button"
															>
																ล้างทั้งหมด
															</Button>
														)}
													</div>
												</PopoverContent>
											</Popover>
										</FormControl>
										<FormDescription>
											เลือกค่ายที่อนุญาตให้เล่นในทัวร์นาเมนต์
											(ไม่เลือกหมายถึงอนุญาตทุกค่าย)
										</FormDescription>
										{field.value && field.value.length > 0 && (
											<div className="mt-2 flex flex-wrap gap-2">
												{field.value.map((providerId) => {
													const provider = providersData?.data.find(
														(p) => p.id === providerId,
													)
													return (
														<Badge key={providerId} variant="secondary">
															{provider?.displayName ||
																provider?.name ||
																providerId}
															<button
																type="button"
																className="ml-1 hover:text-destructive"
																onClick={() => {
																	const newValue = field.value?.filter(
																		(id) => id !== providerId,
																	)
																	field.onChange(newValue)
																}}
															>
																<X className="h-3 w-3" />
															</button>
														</Badge>
													)
												})}
											</div>
										)}
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="allowedGameIds"
								render={({ field }) => (
									<FormItem>
										<FormLabel>เกมที่อนุญาต</FormLabel>
										<FormControl>
											<Popover>
												<PopoverTrigger asChild>
													<Button
														variant="outline"
														role="combobox"
														className="w-full justify-between"
													>
														<span className="truncate">
															{field.value && field.value.length > 0
																? `เลือกแล้ว ${field.value.length} เกม`
																: 'เลือกเกม (ไม่เลือก = อนุญาตทุกเกม)'}
														</span>
													</Button>
												</PopoverTrigger>
												<PopoverContent className="w-full p-0" align="start">
													<div className="space-y-2 p-2">
														<Input
															placeholder="ค้นหาเกม..."
															className="h-8 text-sm"
															value={gameSearchQuery}
															onChange={(e) =>
																setGameSearchQuery(e.target.value)
															}
														/>
														<div className="max-h-[300px] space-y-1 overflow-y-auto">
															{gamesData?.data.map((game) => (
																<div
																	key={game.id}
																	className="flex cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent"
																	onClick={() => {
																		const currentValue = field.value || []
																		const newValue = currentValue.includes(
																			game.id,
																		)
																			? currentValue.filter(
																					(id) => id !== game.id,
																				)
																			: [...currentValue, game.id]
																		field.onChange(newValue)
																	}}
																>
																	<Checkbox
																		checked={field.value?.includes(game.id)}
																		className="pointer-events-none h-4 w-4"
																	/>
																	<span className="text-xs">
																		{game.displayName || game.name}
																		{game.providerName && (
																			<span className="ml-2 text-muted-foreground">
																				({game.providerName})
																			</span>
																		)}
																	</span>
																</div>
															))}
															{(!gamesData?.data ||
																gamesData.data.length === 0) && (
																<div className="py-2 text-center text-xs text-muted-foreground">
																	ไม่พบเกม
																</div>
															)}
														</div>
														{field.value && field.value.length > 0 && (
															<Button
																variant="ghost"
																className="w-full justify-center text-xs"
																onClick={() => field.onChange([])}
																type="button"
															>
																ล้างทั้งหมด
															</Button>
														)}
													</div>
												</PopoverContent>
											</Popover>
										</FormControl>
										<FormDescription>
											เลือกเกมเฉพาะที่อนุญาตให้เล่นในทัวร์นาเมนต์
											(ไม่เลือกหมายถึงอนุญาตทุกเกม)
										</FormDescription>
										{field.value && field.value.length > 0 && (
											<div className="mt-2 flex flex-wrap gap-2">
												{field.value.map((gameId) => {
													const game = gamesData?.data.find(
														(g) => g.id === gameId,
													)
													return (
														<Badge key={gameId} variant="secondary">
															{game?.displayName || game?.name || gameId}
															<button
																type="button"
																className="ml-1 hover:text-destructive"
																onClick={() => {
																	const newValue = field.value?.filter(
																		(id) => id !== gameId,
																	)
																	field.onChange(newValue)
																}}
															>
																<X className="h-3 w-3" />
															</button>
														</Badge>
													)
												})}
											</div>
										)}
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="space-y-4">
							<FormField
								control={form.control}
								name="hasPointSystem"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
										<div className="space-y-0.5">
											<FormLabel className="text-base">
												ระบบคะแนนโบนัส (Streak Bonus)
											</FormLabel>
											<FormDescription>
												เพิ่มคะแนนโบนัสเมื่อชนะติดต่อกัน
											</FormDescription>
										</div>
										<FormControl>
											<Switch
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
									</FormItem>
								)}
							/>

							{form.watch('hasPointSystem') && (
								<div className="space-y-4 rounded-lg border p-4">
									<h4 className="font-medium">ตั้งค่าโบนัสแต้มชนะติดต่อกัน</h4>
									<div className="grid grid-cols-5 gap-4">
										<FormField
											control={form.control}
											name="pointSystemConfig.streakBonuses.3"
											render={({ field }) => (
												<FormItem>
													<FormLabel>ชนะ 3 ครั้ง</FormLabel>
													<FormControl>
														<Input
															type="number"
															placeholder="1"
															value={field.value ?? 1}
															onChange={(e) =>
																field.onChange(Number(e.target.value))
															}
														/>
													</FormControl>
													<FormDescription>+แต้ม</FormDescription>
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="pointSystemConfig.streakBonuses.5"
											render={({ field }) => (
												<FormItem>
													<FormLabel>ชนะ 5 ครั้ง</FormLabel>
													<FormControl>
														<Input
															type="number"
															placeholder="2"
															value={field.value ?? 2}
															onChange={(e) =>
																field.onChange(Number(e.target.value))
															}
														/>
													</FormControl>
													<FormDescription>+แต้ม</FormDescription>
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="pointSystemConfig.streakBonuses.7"
											render={({ field }) => (
												<FormItem>
													<FormLabel>ชนะ 7 ครั้ง</FormLabel>
													<FormControl>
														<Input
															type="number"
															placeholder="3"
															value={field.value ?? 3}
															onChange={(e) =>
																field.onChange(Number(e.target.value))
															}
														/>
													</FormControl>
													<FormDescription>+แต้ม</FormDescription>
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="pointSystemConfig.streakBonuses.10"
											render={({ field }) => (
												<FormItem>
													<FormLabel>ชนะ 10 ครั้ง</FormLabel>
													<FormControl>
														<Input
															type="number"
															placeholder="5"
															value={field.value ?? 5}
															onChange={(e) =>
																field.onChange(Number(e.target.value))
															}
														/>
													</FormControl>
													<FormDescription>+แต้ม</FormDescription>
												</FormItem>
											)}
										/>
										<FormField
											control={form.control}
											name="pointSystemConfig.streakBonuses.15"
											render={({ field }) => (
												<FormItem>
													<FormLabel>ชนะ 15 ครั้ง</FormLabel>
													<FormControl>
														<Input
															type="number"
															placeholder="10"
															value={field.value ?? 10}
															onChange={(e) =>
																field.onChange(Number(e.target.value))
															}
														/>
													</FormControl>
													<FormDescription>+แต้ม</FormDescription>
												</FormItem>
											)}
										/>
									</div>
									<p className="text-sm text-muted-foreground">
										ตัวอย่าง: หากตั้งค่า &ldquo;ชนะ 5 ครั้ง = +2 แต้ม&rdquo;
										ผู้เล่นที่ชนะ 5 ครั้งติดต่อกันจะได้แต้มโบนัสเพิ่ม 2 แต้ม
									</p>
								</div>
							)}
						</div>

						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-semibold">รางวัล</h3>
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() =>
										append({ rank: fields.length + 1, prizeAmount: 0 })
									}
								>
									<Plus className="mr-2 h-4 w-4" />
									เพิ่มรางวัล
								</Button>
							</div>

							{fields.map((field, index) => (
								<Card key={field.id}>
									<CardContent className="pt-6">
										<div className="grid grid-cols-[1fr_2fr_auto] gap-4">
											<FormField
												control={form.control}
												name={`prizes.${index}.rank`}
												render={({ field }) => (
													<FormItem>
														<FormLabel required>อันดับที่</FormLabel>
														<FormControl>
															<Input
																type="number"
																placeholder="1"
																{...field}
																onNumberChange={(value) =>
																	field.onChange(value)
																}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>

											<FormField
												control={form.control}
												name={`prizes.${index}.prizeAmount`}
												render={({ field }) => (
													<FormItem>
														<FormLabel required>
															จำนวนเงินรางวัล (บาท)
														</FormLabel>
														<FormControl>
															<Input
																type="number"
																placeholder="0"
																{...field}
																onNumberChange={(value) =>
																	field.onChange(value)
																}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>

											<div className="flex items-end">
												<Button
													type="button"
													variant="ghost"
													size="icon"
													onClick={() => remove(index)}
													disabled={fields.length === 1}
												>
													<Trash2 className="h-4 w-4 text-destructive" />
												</Button>
											</div>
										</div>
									</CardContent>
								</Card>
							))}
						</div>

						<div className="flex gap-4">
							{onCancel && (
								<Button type="button" variant="outline" onClick={onCancel}>
									ยกเลิก
								</Button>
							)}
							<Button type="submit" disabled={isPending}>
								{isPending
									? mode === 'create'
										? 'กำลังสร้าง...'
										: 'กำลังอัปเดต...'
									: mode === 'create'
										? 'สร้างทัวร์นาเมนต์'
										: 'อัปเดตทัวร์นาเมนต์'}
							</Button>
							{mode === 'create' && !onCancel && (
								<Button
									type="button"
									variant="outline"
									onClick={() => form.reset()}
								>
									ล้างข้อมูล
								</Button>
							)}
						</div>
					</form>
				</Form>
			</CardContent>
		</Card>
	)
}
